// ─────────────────────────────────────────────────────────────────────────────
// Acceso a los datasets de geocercas de `public/data/geofences` (§3.3 del plan
// de expansión a Argentina).
//
// Estructura en disco:
//   public/data/geofences/manifest.json          { generatedAt, attribution, countries }
//   public/data/geofences/cl/{01..16}.geojson
//   public/data/geofences/ar/{02..94}.geojson
//
// El manifest indexa los países bajo la clave `countries`:
//   countries: { cl: [ { regionCode, dbRegionCode, name, file, featureCount } ], ar: [...] }
//
// Cada feature de los geojson expone propiedades UNIFORMES entre países:
//   { name, code, region, regionCode, labelX, labelY }
// (`labelX/labelY` = punto interior precalculado por el pipeline para rotular
// sin calcular centroides en runtime).
//
// Este módulo es el ÚNICO punto de fetch de geocercas de la app. Cachea a nivel
// de módulo — no de estado de React — porque el árbol de providers se remonta en
// cada cambio de ruta y un cache de componente no sobreviviría a la navegación.
// La identidad del objeto GeoJSON cacheado importa además para `geoUtils`, que
// cuelga de él (por WeakMap) el índice espacial: re-parsear el mismo archivo
// obligaría a reconstruir el índice.
// ─────────────────────────────────────────────────────────────────────────────

import { normalizeString } from './geoUtils';

const BASE_URL = `${process.env.PUBLIC_URL}/data/geofences`;

/** Promise<manifest> en vuelo o resuelta. */
let manifestPromise = null;
/** `${countryCode}/${regionCode}` → Promise<FeatureCollection> */
const regionPromises = new Map();

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`geofences: ${url} → HTTP ${res.status}`);
  return res.json();
}

const countryKey = (countryCode) => String(countryCode || '').toLowerCase();

/**
 * Manifest de geocercas. Una sola request por sesión; ante un fallo se limpia la
 * promesa cacheada para que un reintento del usuario pueda recuperarse (si no,
 * un corte de red momentáneo dejaría la vista rota para siempre).
 *
 * @returns {Promise<{generatedAt?: string, attribution?: string, countries: object}>}
 */
export function loadManifest() {
  if (!manifestPromise) {
    manifestPromise = fetchJson(`${BASE_URL}/manifest.json`).catch((err) => {
      manifestPromise = null;
      throw err;
    });
  }
  return manifestPromise;
}

/**
 * Regiones disponibles para un país, en el orden del manifest.
 *
 * @param {string} countryCode ISO 3166-1 alpha-2 ('CL', 'AR')
 * @returns {Promise<Array<{regionCode: string, dbRegionCode: string, name: string, file: string, featureCount: number}>>}
 *   Lista vacía si el país no tiene datasets generados (no lanza por eso: un
 *   país sin geocercas es un estado normal de la app, no un error).
 */
export async function getRegionsForCountry(countryCode) {
  const key = countryKey(countryCode);
  if (!key) return [];
  const manifest = await loadManifest();
  const regions = manifest?.countries?.[key];
  return Array.isArray(regions) ? regions : [];
}

/**
 * GeoJSON de una región. Cacheado en memoria por (país, región): cambiar de
 * región y volver no vuelve a descargar ni a re-parsear.
 *
 * @param {string} countryCode ISO 3166-1 alpha-2
 * @param {string} regionCode `regionCode` del manifest (NO el `dbRegionCode`)
 * @returns {Promise<object>} FeatureCollection
 * @throws si el país/región no está en el manifest o el fetch falla
 */
export function loadRegionGeojson(countryCode, regionCode) {
  const key = countryKey(countryCode);
  const cacheKey = `${key}/${regionCode}`;

  if (!regionPromises.has(cacheKey)) {
    const promise = getRegionsForCountry(key)
      .then((regions) => {
        const entry = regions.find((r) => String(r.regionCode) === String(regionCode));
        if (!entry) {
          throw new Error(`geofences: región ${regionCode} no está en el manifest de ${key}`);
        }
        return fetchJson(`${BASE_URL}/${entry.file}`);
      })
      .catch((err) => {
        regionPromises.delete(cacheKey);
        throw err;
      });
    regionPromises.set(cacheKey, promise);
  }

  return regionPromises.get(cacheKey);
}

// ── Resolución de la región por defecto ──────────────────────────────────────

// Prefijos administrativos que el nombre del manifest trae y el dato del
// operador normalmente no ("Región de Coquimbo" vs "Coquimbo").
const DIVISION_PREFIX = /^(region|provincia)\s+(de\s+la\s+|de\s+los\s+|de\s+las\s+|del\s+|de\s+)?/;

// Región por defecto cuando no se puede inferir del domicilio del operador.
// Chile: 13 (Metropolitana) — es la única región que el mapa cubría antes de
// esta iniciativa (public/data/rm-comunas.geojson), así que preserva la vista
// histórica para todos los operadores chilenos. Los demás países caen a la
// primera región del manifest (no hay un default con significado operativo).
const DEFAULT_REGION_BY_COUNTRY = { CL: '13' };

/** Nombre normalizado sin el prefijo administrativo ("región de", "provincia"). */
function strippedName(name) {
  return normalizeString(name).replace(DIVISION_PREFIX, '').trim();
}

/**
 * Nombre de región apto para texto corrido: sin el prefijo administrativo pero
 * conservando tildes, mayúsculas y —a diferencia de `strippedName`— el artículo,
 * que forma parte del nombre propio ("Región de Los Lagos" → "Los Lagos", no
 * "Lagos"). "Región Metropolitana de Santiago" → "Metropolitana de Santiago";
 * "Mendoza" → "Mendoza".
 *
 * @param {string} name nombre del manifest
 * @returns {string}
 */
export function shortRegionName(name) {
  const raw = String(name ?? '').trim();
  if (!raw) return '';
  const short = raw.replace(/^(Regi[oó]n|Provincia)\s+(del\s+|de\s+)?/i, '').trim();
  return short || raw;
}

/**
 * Busca en el manifest la región que corresponde a un nombre libre.
 *
 * El dato de entrada es texto escrito a mano o devuelto por el geocoder
 * (`operationsAddresses[].state` es un input libre de /admin/settings), así que
 * llega como "Metropolitana", "Región Metropolitana", "RM", "Santiago" o
 * "Mendoza". La resolución va de lo estricto a lo laxo y ABANDONA ante
 * ambigüedad: un default equivocado es peor que ningún default, porque el
 * operador ve conteos que no son los suyos sin saber por qué.
 *
 * @param {Array<object>} regions entradas del manifest
 * @param {string} rawName nombre libre de la región
 * @returns {object|null} la entrada del manifest, o null si no hay match único
 */
export function matchRegionByName(regions, rawName) {
  const query = normalizeString(rawName);
  if (!query || !Array.isArray(regions) || !regions.length) return null;
  const queryStripped = strippedName(rawName);

  // 1. Código exacto ("13", "RM", "50").
  const byCode = regions.find(
    (r) =>
      normalizeString(r.regionCode) === query || normalizeString(r.dbRegionCode) === query,
  );
  if (byCode) return byCode;

  // 2. Nombre exacto, con y sin prefijo administrativo.
  const exact = regions.filter((r) => {
    const full = normalizeString(r.name);
    const bare = strippedName(r.name);
    return full === query || bare === query || full === queryStripped || bare === queryStripped;
  });
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;

  // 3. Contención, solo si es inequívoca. El mínimo de 4 caracteres evita que
  //    fragmentos como "los" o "san" arrastren a una región cualquiera.
  if (queryStripped.length < 4) return null;
  const contained = regions.filter((r) => {
    const bare = strippedName(r.name);
    return bare.includes(queryStripped) || queryStripped.includes(bare);
  });
  return contained.length === 1 ? contained[0] : null;
}

/**
 * Región por defecto de la vista de análisis.
 *
 * Cascada: domicilio operativo del operador → default del país → primera del
 * manifest. (La opción "primera región con datos del período" se descartó a
 * propósito: obligaría a descargar las geocercas de TODO el país —hasta ~6,4 MB
 * en Chile— solo para elegir un valor inicial que el selector deja cambiar en un
 * clic.)
 *
 * @param {Array<object>} regions entradas del manifest
 * @param {{countryCode?: string, stateName?: string}} ctx
 * @returns {string} `regionCode`, o '' si no hay regiones
 */
export function resolveDefaultRegion(regions, { countryCode, stateName } = {}) {
  if (!Array.isArray(regions) || !regions.length) return '';

  const fromAddress = matchRegionByName(regions, stateName);
  if (fromAddress) return fromAddress.regionCode;

  const preferred = DEFAULT_REGION_BY_COUNTRY[String(countryCode || '').toUpperCase()];
  if (preferred && regions.some((r) => String(r.regionCode) === preferred)) return preferred;

  return regions[0].regionCode;
}
